// Per-copy writes: add, patch (qty, status, attributes, split), delete, bulk,
// the Undo of a delete (plan 2026-10-16 W2, C3, C5, C6, R2-1), and take
// (remove or move N copies per line from a pasted list) with its server-owned
// Undo (mtg-api plan 2026-10-30 W2).
//
// Every write, in this order:
//  1. refuses unless the capability is per_copy;
//  2. captures the write fence (tryBeginCopiesWrite) BEFORE any optimistic
//     change: refused while a Remove is queued or running, or when the live
//     user is not the owner the page is for;
//  3. applies the optimistic change (qty, status, removal), keeping a
//     snapshot of every affected query to roll back;
//  4. sends with ONE op_key per user action (add, patch, bulk; DELETE takes
//     none), re-sent unchanged ONLY to retry that action after an ambiguous
//     failure (network or 5xx), at most twice;
//  5. re-checks `fenced()` after every await and before applying anything: a
//     fenced response is dropped and the optimistic change rolled back;
//  6. on success invalidates the collection's copies, card, info and value
//     queries, and, when `names_changed` is true or the write was a delete,
//     schedules ONE owned-names revalidation (debounced 1.5 s, coalesced).
//     The ownership epoch moves only when the name SET changed: that rule is
//     useCollection.saveCollection's (sameNames), which the revalidation's
//     canonical commit goes through.
//
// Failures come back as values, not throws, so a screen can render each one:
//  - copy_not_found (R2-1): ITEM error; the optimistic change is dropped and
//    the list refetched; the capability is unchanged.
//  - op_key_in_use: a bug signal; logged, refetched, generic error.
//  - a collection-level 404 etc.: the capability store already downgraded
//    the pair (api/collectionCopies.ts); the page falls back to name-level.
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query'
import {
  CopiesError, addCopies, bulkCopies, canonicalFilterJson, canonicalizeFilters, deleteCopy, isCopiesError, newOpKey, patchCopy, takeCopies, undoTake,
  type AddItem, type AddResponse, type BulkResponse, type BulkValue, type CopiesContext, type CopiesPage,
  type CopyItem, type PatchBody, type PatchResponse, type TakeItem, type TakeResponse, type TakeUndoResponse,
} from '../api/collectionCopies'
import {
  ADD_SOURCES, CAPABILITY, DETAIL, ERR, ITEM_ERROR_CODES, OP_KIND, QK, WRITE_FAIL, type AddSource, type BulkAction, type CopyStatus, type TakeAction,
} from '../api/collectionCopies.codes'
import { getCapability, isDowngraded, subscribeCapability } from '../lib/copiesCapability'
import { captureCopiesSession, captureCopiesWriteContext, noteOwnCopiesChange, tryBeginCopiesWrite } from '../lib/collectionWriteFence'
import {
  clearCopyLineEdits, currentIncarnation, deleteLineEdits, getLineEdits, lineEditsEpoch, lineEditsKey, lineEditsLive, setLineEdits,
  type LineEditsState,
} from '../lib/copyLineEdits'
import { addRetryIntent, clearRetryIntents, emitIntentSettled, hasUnresolvedIntent, __resetRetryIntents, type RetryIntent } from '../lib/copiesRetryIntents'
import { getSupabaseIdentity } from './useSupabaseToken'
import { collectionOwnerOf } from '../lib/collectionOwner'
import { requestCollectionRevalidate } from './useCollection'
import { QK_BINDER_LAYOUT } from '../api/binderLayout.codes'

export const OWNED_NAMES_REVALIDATE_DEBOUNCE_MS = 1_500
const MAX_ACTION_RETRIES = 2

// --- Owned-names revalidation: debounced and coalesced ------------------------

let revalidateTimer: ReturnType<typeof setTimeout> | null = null

/** Ask CollectionSync for ONE owned-names pass 1.5 s after the last call. */
export function scheduleOwnedNamesRevalidate(): void {
  if (revalidateTimer !== null) clearTimeout(revalidateTimer)
  revalidateTimer = setTimeout(() => {
    revalidateTimer = null
    requestCollectionRevalidate()
  }, OWNED_NAMES_REVALIDATE_DEBOUNCE_MS)
}

/** Test seam. */
export function __resetOwnedNamesRevalidate(): void {
  if (revalidateTimer !== null) clearTimeout(revalidateTimer)
  revalidateTimer = null
}

// --- Ambiguous writes: pending, then reconciled ------------------------------
//
// A write whose outcome is AMBIGUOUS (a transport failure or a 5xx after its
// immediate retries: the server may have committed it) is NOT reported as a
// failure and NOT rolled back. Its optimistic state stays, its lines show as
// pending ("Saving..."), and the SAME request (same op_key; a DELETE is
// idempotent) is re-sent with backoff until the server answers:
//  - success: the write resolves as a success (no error is ever shown; a
//    delete's Undo appears only now);
//  - a definitive 4xx: roll back and report that error;
//  - no answer after the last attempt: roll back, refetch server truth, and
//    report "could not confirm" with a Retry that reuses the op_key.
// The write stays registered for Remove exclusion the whole time (Remove
// fails retryably meanwhile), and the per-copy queue holds later edits to the
// same line behind it. The reconcile is deliberately NOT cancelled by
// Remove's abort.

const RECONCILE = { confirmed: 'confirmed', definitive: 'definitive', gaveUp: 'gave_up', identity: 'identity' } as const
type ReconcileOutcome<T> =
  | { kind: typeof RECONCILE.confirmed; value: T }
  | { kind: typeof RECONCILE.definitive; error: CopiesError }
  | { kind: typeof RECONCILE.gaveUp }
  | { kind: typeof RECONCILE.identity }

// Lines under reconcile, for the per-row "Saving..." indicator.
const pendingLines = new Map<string, number>()
const pendingListeners = new Set<() => void>()

function setPending(collectionId: string, ids: number[] | undefined, on: boolean): void {
  if (!ids?.length) return
  for (const id of ids) {
    const key = queueKey(collectionId, id)
    const n = (pendingLines.get(key) ?? 0) + (on ? 1 : -1)
    if (n > 0) pendingLines.set(key, n)
    else pendingLines.delete(key)
  }
  for (const l of pendingListeners) l()
}

export function isCopyPending(collectionId: string | null, copyId: number): boolean {
  return !!collectionId && pendingLines.has(queueKey(collectionId, copyId))
}

/** True while this copy line has a write being reconciled ("Saving..."). */
export function useCopyPending(collectionId: string | null, copyId: number): boolean {
  return useSyncExternalStore(
    cb => { pendingListeners.add(cb); return () => { pendingListeners.delete(cb) } },
    () => isCopyPending(collectionId, copyId),
    () => false,
  )
}

const RECONCILE_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000]
/** ONE absolute deadline for the whole reconcile (performance.now based). */
export const RECONCILE_DEADLINE_MS = 60_000
/** Each reconcile attempt is a single request aborted after this long. */
export const RECONCILE_REQUEST_TIMEOUT_MS = 15_000
/**
 * Each attempt of the ORIGINAL write is aborted after this long and treated
 * as ambiguous (a browser fetch has no timeout of its own, and a hung request
 * would otherwise hold the Remove exclusion forever).
 */
export const WRITE_REQUEST_TIMEOUT_MS = 30_000

let reconcileNow: () => number = () => performance.now()

/** Test seam: the monotonic clock the reconcile deadline is measured on. */
export function __setReconcileClock(fn: (() => number) | null): void {
  reconcileNow = fn ?? (() => performance.now())
}

/** Test seam: reconcile sleeps advance a virtual clock instantly. */
export function __useVirtualReconcileTime(): void {
  let now = 0
  reconcileSleep = async ms => { now += ms }
  reconcileNow = () => now
}
type ReconcileSleep = (ms: number) => Promise<void>
const realReconcileSleep: ReconcileSleep = ms => new Promise(r => setTimeout(r, ms))
let reconcileSleep: ReconcileSleep = realReconcileSleep

/** Test seam: replace the reconcile backoff sleep. */
export function __setReconcileSleep(fn: ReconcileSleep | null): void {
  reconcileSleep = fn ?? realReconcileSleep
}

function isAmbiguous(kind: string): boolean {
  return kind === ERR.network || kind === ERR.server
}

/**
 * During the reconcile, everything that is not an authoritative answer about
 * THIS request keeps it pending: transport failures, 5xx, a timeout or other
 * abort (including one caused by a capability downgrade: the per-copy UI may
 * be gone, but the request may still land), rate limiting or an auth outage,
 * an unreadable body, or a session that could not be read for this attempt.
 */
function isReconcileAmbiguous(kind: string): boolean {
  return isAmbiguous(kind)
    || kind === ERR.aborted || kind === ERR.rateLimited || kind === ERR.serviceUnavailable
    || kind === ERR.decode || kind === ERR.authRefused || kind === ERR.unauthorized
}

// Retry intents (unconfirmed changes kept until dismissed or settled) live in
// src/lib/copiesRetryIntents.ts, so Remove, sign-out and account deletion can
// clear them without importing this hook. Re-exported for the screens.
export {
  ackRetryIntent, clearRetryIntents, hasUnresolvedIntent, peekRetryIntents, subscribeIntentSettled, useLineHasUnresolvedIntent, useRetryIntents,
  type IntentSettled, type RetryIntent,
} from '../lib/copiesRetryIntents'

// --- Per-copy serialization -------------------------------------------------
//
// Every write to ONE copy line (qty, status, attributes, split, delete) runs
// after the previous one for that line has settled, and a relative qty change
// (adjustQty) is derived from the latest PENDING absolute value, not from the
// rendered one: two fast "+" taps on qty N send N+1 then N+2, never N+1 twice.

interface CopyQueue { tail: Promise<unknown>; pending: number; qty: number | null }
const copyQueues = new Map<string, CopyQueue>()

function queueKey(collectionId: string, copyId: number): string {
  return `${collectionId}#${copyId}`
}

function enqueue<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const q = copyQueues.get(key) ?? { tail: Promise.resolve(), pending: 0, qty: null }
  copyQueues.set(key, q)
  q.pending += 1
  const p = q.tail.then(fn, fn)
  q.tail = p.catch(() => undefined)
  void p.finally(() => {
    q.pending -= 1
    if (q.pending === 0 && copyQueues.get(key) === q) copyQueues.delete(key)
  }).catch(() => undefined)
  return p
}

// Per line: the CONFIRMED baseline (the line as the server last agreed to
// it) and the PATCHes queued on it and not yet settled, in order. What the
// line shows is always the baseline with every pending change applied
// (displayedLine), so a change shows as soon as it is queued, a confirmed
// change moves the baseline, and a change that does not land is simply
// removed and the line recomputed: never a value captured when it was
// queued, which may itself have been unconfirmed (Codex r1 #3).
// The state itself lives in lib/copyLineEdits, bound to the owner, the
// session epoch, the offline incarnation and the write-fence capture it was
// made under (Codex r2 #5, #6): every cache write from it checks that it is
// still live first.
interface LineEdit { seq: number; body: PatchBody; display?: PrintingDisplay }
/** `touched`: every field a change of this run touched, what the display owns (a removed change's fields go back to the baseline). */
type LineEdits = LineEditsState<CopyItem, LineEdit>
let lineEditSeq = 0

/**
 * The owner session an operation (or an optimistic snapshot) was made in
 * (Codex r3): the live owner, the collection pointer and its identity, the
 * clear generation (collectionWriteFence.captureCopiesSession), the
 * line-state epoch (bumped by the owner purge and every owner change) and
 * the offline owner incarnation. Dead for good once any of them moves.
 */
interface SessionBinding { live: () => boolean }
const DEAD_SESSION: SessionBinding = { live: () => false }

function sessionBinding(userId: string | null, collectionId: string | null): SessionBinding {
  if (!userId || !collectionId) return DEAD_SESSION
  const session = captureCopiesSession(userId, collectionId)
  const epochAt = lineEditsEpoch()
  const incarnationAt = currentIncarnation()
  return { live: () => session.live() && lineEditsEpoch() === epochAt && currentIncarnation() === incarnationAt }
}

/**
 * Queue `fn` on the line, bound to the session it was queued in: when its
 * turn comes in a dead session (a purge, another account, the collection
 * replaced) it is REFUSED (`blocked`) without running: no optimistic change,
 * nothing sent. A live one gets the binding to re-check before it sends.
 */
function enqueueBound<R>(key: string, binding: SessionBinding, fn: (binding: SessionBinding) => Promise<R>): Promise<R | WriteFailure> {
  return enqueue<R | WriteFailure>(key, () => (binding.live() ? fn(binding) : Promise.resolve(BLOCKED)))
}

const BLOCKED: WriteFailure = { ok: false, reason: 'blocked' }

/** Test seam. */
export function __resetCopyQueues(): void {
  copyQueues.clear()
  clearCopyLineEdits()
  pendingLines.clear()
  __resetRetryIntents()
}

// --- Results --------------------------------------------------------------------

export type WriteFailure =
  /** Not sent (or its result dropped): not per_copy, Remove in progress, or the account changed. */
  | { ok: false; reason: 'blocked' }
  | {
      ok: false; reason: 'error'; error: CopiesError
      /** For an UNCONFIRMED change only: re-run it with the SAME op_key. */
      retry?: () => Promise<RetryResult>
      /** For an UNCONFIRMED change: the kept intent it was registered as. */
      intentId?: number
    }

/**
 * What a Retry resolves to: the operation's own response in `value` (a
 * PatchResponse, BulkResponse, AddResponse...) and a confirmed removal.
 */
export type RetryResult = { ok: true; value?: unknown; removed?: RemovedCopy } | WriteFailure

export type WriteResult<T> = { ok: true; value: T } | WriteFailure

/** A removed line, held so Undo can put it back (C5). */
export interface RemovedCopy {
  item: CopyItem
}

export type RemoveResult = { ok: true; removed: RemovedCopy } | WriteFailure

// --- Optimistic cache edits -----------------------------------------------------

type ItemsData = InfiniteData<CopiesPage, unknown> | CopyItem[]

function mapItems(data: ItemsData | undefined, fn: (items: CopyItem[]) => CopyItem[]): ItemsData | undefined {
  if (!data) return data
  if (Array.isArray(data)) return fn(data)
  return { ...data, pages: data.pages.map(p => ({ ...p, items: fn(p.items) })) }
}

type Snapshot = [QueryKey, unknown][]

function snapshotOf(qc: QueryClient, collectionId: string): Snapshot {
  return [
    ...qc.getQueriesData({ queryKey: [QK.copies, collectionId] }),
    ...qc.getQueriesData({ queryKey: [QK.copiesDetail, collectionId] }),
  ]
}

function restore(qc: QueryClient, snap: Snapshot): void {
  for (const [key, data] of snap) qc.setQueryData(key, data)
}

/** The canonical filter JSON of a list with no filter at all. */
const UNFILTERED = canonicalFilterJson(canonicalizeFilters({}))

/**
 * The optimistic edit (Codex r1 #4 on the deck-builder search). A line edited
 * in place is only right where membership cannot depend on the edit: the
 * UNFILTERED list and a card's own lines (the detail caches, keyed by name).
 * A filtered list (status tab, qty_min, price, syntax, a server group) could
 * now be wrong about whether the line belongs, and about its matched_qty and
 * facets, so it is NOT edited: it and the facets are invalidated at once and
 * refetch the server's answer.
 */
export function editItems(
  qc: QueryClient, collectionId: string, fn: (items: CopyItem[]) => CopyItem[], opts: { deferRefetch?: boolean; owner?: string } = {},
): void {
  // deferRefetch (an OPTIMISTIC edit, before its write is sent): mark the
  // filtered lists and the facets stale but do not read them now. A read
  // sent before the write lands can only return the old answer, and the
  // write's own confirmation re-reads them anyway (two reads per edit, the
  // first wasted). After a confirmed answer (a 409's current_qty) they are
  // read at once, as before.
  // `owner`: only that owner's entries (every list key is [root, collection, user, ...]).
  const refetchType = opts.deferRefetch ? NO_REFETCH : undefined
  const scope = (root: string) => (opts.owner ? [root, collectionId, opts.owner] : [root, collectionId])
  qc.setQueriesData<ItemsData>({ queryKey: scope(QK.copiesDetail) }, d => mapItems(d, fn))
  for (const [key] of qc.getQueriesData({ queryKey: scope(QK.copies) })) {
    if (key[3] === UNFILTERED) qc.setQueryData<ItemsData>(key, d => mapItems(d, fn))
    else void qc.invalidateQueries({ queryKey: key, exact: true, refetchType })
  }
  void qc.invalidateQueries({ queryKey: scope(QK.copiesFacets), refetchType })
}

// eslint-disable-next-line i18next/no-literal-string -- TanStack option value
const NO_REFETCH = 'none' as const

/** An optimistic edit (see editItems). */
function editOptimistic(qc: QueryClient, collectionId: string, fn: (items: CopyItem[]) => CopyItem[]): void {
  editItems(qc, collectionId, fn, { deferRefetch: true })
}

/**
 * The server's qty for a line, from a 409 qty_mismatch (the line was not at
 * the expected_qty the write sent; nothing changed). null for any other
 * failure, or a mismatch without a numeric current_qty.
 */
export function qtyMismatchCurrent(f: WriteFailure | CopiesError): number | null {
  const err = f instanceof CopiesError ? f : f.reason === WRITE_FAIL.error ? f.error : null
  if (!err || err.kind !== ERR.conflict || err.detail !== DETAIL.qtyMismatch) return null
  const current = err.body?.current_qty
  return typeof current === 'number' ? current : null
}

/** True for a 409 qty_mismatch, with or without current_qty. */
export function isQtyMismatch(f: WriteFailure): boolean {
  return f.reason === WRITE_FAIL.error && f.error.kind === ERR.conflict && f.error.detail === DETAIL.qtyMismatch
}

/**
 * After a 409 qty_mismatch on line `copyId`: write the server's current_qty
 * into the cached line at once (0 or less: the line is gone), so the next
 * relative change builds on it rather than on the stale qty. The caller
 * refetches as well (the runner does, on every qty_mismatch).
 */
export function applyCurrentQty(qc: QueryClient, collectionId: string, copyId: number, current: number): void {
  editItems(qc, collectionId, items => (current <= 0
    ? items.filter(i => i.copy_id !== copyId)
    : items.map(i => (i.copy_id === copyId ? { ...i, qty: current } : i))))
}

/**
 * What a row shows of a new PRINTING (the picker's printing), applied with a
 * PATCH that changes scryfall_id so the line reads as that printing at once
 * instead of after the write and its refetch. Server truth replaces it on the
 * refetch the confirmed write triggers.
 */
export type PrintingDisplay = Partial<Pick<CopyItem, 'set' | 'set_name' | 'collector_number' | 'rarity' | 'image_normal' | 'unit_price_usd'>>

/** The fields of a PATCH body that change what a row shows. */
function applyPatch(item: CopyItem, body: PatchBody, display?: PrintingDisplay): CopyItem {
  const next = { ...item }
  if (body.scryfall_id !== undefined) {
    next.scryfall_id = body.scryfall_id
    if (display) Object.assign(next, display)
  }
  if (body.qty !== undefined) next.qty = body.qty
  if (body.status !== undefined) next.status = body.status
  if (body.condition !== undefined) next.condition = body.condition
  if (body.language !== undefined) next.language = body.language
  if (body.finish !== undefined) next.finish = body.finish
  if (body.purchase_price !== undefined) next.purchase_price = body.purchase_price
  if (body.purchase_currency !== undefined) next.purchase_currency = body.purchase_currency
  return next
}

/** The edit one PATCH makes to its line in every cached list (qty 0 removes it). */
function lineEdit(copyId: number, body: PatchBody, display?: PrintingDisplay): (items: CopyItem[]) => CopyItem[] {
  return items => (body.qty === 0
    ? items.filter(i => i.copy_id !== copyId)
    : items.map(i => (i.copy_id === copyId ? applyPatch(i, body, display) : i)))
}

/** The line as the cache holds it now (a card's lines first, else any list page). */
function cachedLine(qc: QueryClient, collectionId: string, copyId: number): CopyItem | undefined {
  for (const root of [QK.copiesDetail, QK.copies]) {
    for (const [, data] of qc.getQueriesData<ItemsData>({ queryKey: [root, collectionId] })) {
      const items = !data ? [] : Array.isArray(data) ? data : data.pages.flatMap(p => p.items)
      const hit = items.find(i => i.copy_id === copyId)
      if (hit) return hit
    }
  }
  return undefined
}

/* eslint-disable i18next/no-literal-string -- CopyItem field names */
/** The printing fields a scryfall_id change (and its display) moves. */
const PRINTING_FIELDS = ['scryfall_id', 'set', 'set_name', 'collector_number', 'rarity', 'image_normal', 'unit_price_usd'] as const

/** The line fields one PATCH body changes. */
function fieldsOf(body: PatchBody): (keyof CopyItem)[] {
  const out: (keyof CopyItem)[] = []
  if (body.qty !== undefined) out.push('qty')
  if (body.status !== undefined) out.push('status')
  if (body.condition !== undefined) out.push('condition')
  if (body.language !== undefined) out.push('language')
  if (body.finish !== undefined) out.push('finish')
  if (body.purchase_price !== undefined) out.push('purchase_price')
  if (body.purchase_currency !== undefined) out.push('purchase_currency')
  if (body.scryfall_id !== undefined) out.push(...PRINTING_FIELDS)
  return out
}
/* eslint-enable i18next/no-literal-string */

/** The line as it should show: the confirmed baseline with every pending change applied, in order. */
export function displayedLine(state: Pick<LineEdits, 'base' | 'edits'>): CopyItem {
  return state.edits.reduce((line, e) => applyPatch(line, e.body, e.display), state.base)
}

/** Put the displayed line's owned fields into every cached copy of the line (`beforeSeq`: only the changes queued before it). */
function showLine(qc: QueryClient, collectionId: string, state: LineEdits, beforeSeq?: number): void {
  const shown = displayedLine(beforeSeq === undefined ? state : { base: state.base, edits: state.edits.filter(e => e.seq < beforeSeq) })
  const copyId = state.base.copy_id
  // Only the state's own owner's cached lists: never another account's entries.
  editItems(qc, collectionId, items => items.map(i => {
    if (i.copy_id !== copyId) return i
    const next = { ...i }
    for (const k of state.touched) (next as Record<string, unknown>)[k] = shown[k as keyof CopyItem]
    return next
  }), { deferRefetch: true, owner: state.owner })
}

/** The add item that re-creates a removed line (Undo). */
export function addItemFor(item: CopyItem): AddItem {
  const out: AddItem = { qty: item.qty, status: item.status }
  if (item.scryfall_id) {
    out.scryfall_id = item.scryfall_id
    out.finish = item.finish
  } else {
    out.name = item.card_name
  }
  if (item.condition) out.condition = item.condition
  if (item.language) out.language = item.language
  if (item.purchase_price) out.purchase_price = item.purchase_price
  if (item.purchase_currency) out.purchase_currency = item.purchase_currency
  // Back into the binder it was in (Unsorted lines carry none).
  if (typeof item.binder_id === 'number') out.binder_id = item.binder_id
  return out
}

/** qty 0 deleted the line: it can be undone, like a delete (C5). */
function withRemoved(r: WriteResult<PatchResponse>, item: CopyItem): WriteResult<PatchResponse> & { removed?: RemovedCopy } {
  return r.ok && r.value.deleted ? { ...r, removed: { item } } : r
}

/**
 * Settle `p` within `ms`, or reject: a HARD bound on one attempt, because
 * aborting the signal does not cancel everything an attempt awaits (reading
 * or refreshing the Supabase session cannot be cancelled). The controller is
 * aborted on timeout (and the race also settles on any other abort of it), so
 * the abandoned attempt can never send its request afterwards: copiesFetch
 * checks the signal after reading the session and before every fetch.
 */
function boundedAttempt<T>(p: Promise<T>, controller: AbortController, ms: number): { promise: Promise<T>; timedOut: () => boolean } {
  let timedOut = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let onAbort: (() => void) | undefined
  const stop = new Promise<never>((_r, reject) => {
    timer = setTimeout(() => { timedOut = true; controller.abort(); reject(new CopiesError(ERR.network)) }, ms)
    // On any other abort, give the attempt one macrotask to settle first: an
    // attempt whose own answer CAUSED the abort (a 404 that downgrades the
    // pair aborts this signal from inside copiesFetch, just before it throws
    // that authoritative 404) must report its answer, not "aborted".
    onAbort = () => { setTimeout(() => reject(new CopiesError(timedOut ? ERR.network : ERR.aborted)), 0) }
    // eslint-disable-next-line i18next/no-literal-string -- DOM event name
    controller.signal.addEventListener('abort', onAbort, { once: true })
    if (controller.signal.aborted) onAbort()
  })
  const promise = Promise.race([p, stop]).finally(() => {
    if (timer !== undefined) clearTimeout(timer)
    // eslint-disable-next-line i18next/no-literal-string -- DOM event name
    if (onAbort) controller.signal.removeEventListener('abort', onAbort)
  })
  // The loser of the race must not surface as an unhandled rejection.
  p.catch(() => undefined)
  return { promise, timedOut: () => timedOut }
}

/** The roots a confirmed success re-reads: everything when it removed a line. */
function affectsOf<T>(spec: RunSpec<T>, value: T): readonly string[] {
  if (!spec.affects || spec.removedFor?.(value)) return INVALIDATE_ALL
  return spec.affects
}

// --- The hook -------------------------------------------------------------------

export interface SetQtyOpts {
  /** The qty the user saw: sent as expected_qty. */
  expectedQty?: number
}

export interface AdjustQtyOpts {
  /** Send the qty this change builds on as expected_qty. */
  expectQty?: boolean
}

/** What a take's confirmed result is reported with (a move's binder name). */
export interface TakeMeta {
  binderName?: string
}

export interface CopyMutations {
  add: (items: AddItem[], source?: AddSource) => Promise<WriteResult<AddResponse>>
  /** `display`: what the line shows of a new printing (a scryfall_id change), at once. */
  patch: (item: CopyItem, body: Omit<PatchBody, 'op_key'>, display?: PrintingDisplay) => Promise<WriteResult<PatchResponse> & { removed?: RemovedCopy }>
  /** `opts.expectedQty`: sent as expected_qty (409 qty_mismatch if the line no longer has it). */
  setQty: (item: CopyItem, qty: number, opts?: SetQtyOpts) => Promise<WriteResult<PatchResponse> & { removed?: RemovedCopy }>
  /**
   * qty + delta, derived from the latest PENDING value for this line (fast taps add up).
   * `opts.expectQty`: also send that base as expected_qty, so a line another
   * device changed meanwhile is a 409 qty_mismatch, never a blind write.
   */
  adjustQty: (item: CopyItem, delta: number, opts?: AdjustQtyOpts) => Promise<WriteResult<PatchResponse> & { removed?: RemovedCopy }>
  setStatus: (item: CopyItem, status: CopyStatus) => Promise<WriteResult<PatchResponse>>
  split: (item: CopyItem, splitQty: number, changes: Omit<PatchBody, 'op_key' | 'split_qty' | 'qty'>) => Promise<WriteResult<PatchResponse>>
  /** Into a manual binder (or Unsorted with null); `splitQty` moves only that many. */
  move: (item: CopyItem, binderId: number | null, splitQty?: number) => Promise<WriteResult<PatchResponse>>
  remove: (item: CopyItem) => Promise<RemoveResult>
  undo: (removed: RemovedCopy) => Promise<WriteResult<AddResponse>>
  bulk: (copyIds: number[], action: BulkAction, value?: BulkValue) => Promise<WriteResult<BulkResponse>>
  /**
   * Remove or move an exact qty per line (POST /copies/take). `items` name
   * each copy_id once (aggregateTake). One op_key per call, re-sent only by
   * its own retries and its kept Retry intent. `meta` rides on that intent
   * (RetryIntent.meta), so a Retry confirmed after the dialog closed still
   * reports the move's binder name.
   */
  take: (items: TakeItem[], action: TakeAction, binderId?: number | null, meta?: TakeMeta) => Promise<WriteResult<TakeResponse>>
  /** Undo one remove by its token (POST /copies/take/undo), under a FRESH op_key. */
  undoTake: (token: string) => Promise<WriteResult<TakeUndoResponse>>
}

export interface RunSpec<T> {
  optimistic?: () => void
  /** Sends the request. `opKey` is the action's one key (undefined when the route takes none). */
  send: (ctx: CopiesContext, opKey: string | undefined) => Promise<T>
  withOpKey: boolean
  /** Whether this success may have moved the owned-name set. */
  namesChanged: (value: T) => boolean
  /** The copy lines this write touches (per-row pending indicator). */
  pendingIds?: number[]
  /** The one copy line whose queue a Retry of this write must go through. */
  queueCopyId?: number
  /** A confirmed success that removed a line (for the Undo toast). */
  removedFor?: (value: T) => RemovedCopy | undefined
  /** The operation kind, for deduplicating kept Retry intents. */
  kind: string
  /** Kept on a Retry intent for whoever reports its confirmed result (RetryIntent.meta). */
  meta?: unknown
  /**
   * The query roots a CONFIRMED success re-reads (default: every per-copy
   * root, INVALIDATE_ALL). Failures always re-read everything.
   */
  affects?: readonly string[]
  /**
   * After the optimistic snapshot is restored: a line whose changes are
   * tracked (queuePatch) was already showing this change, and the ones queued
   * after it, when the snapshot was taken; this puts it back to its baseline
   * with only the changes queued BEFORE this one.
   */
  afterRollback?: () => void
  /**
   * The operation's own validity beyond the write fence (its queue-time
   * session, a tracked line state): re-checked before the optimistic change
   * and before anything is sent; false refuses it (`blocked`).
   */
  stillValid?: () => boolean
}

/** Every root a copy write can move. */
const INVALIDATE_ALL: readonly string[] = [
  QK.copies, QK.copiesDetail, QK.copiesFacets, QK.collectionInfo, QK.valueHistory, QK.deckLinks, QK.deckOwnership, QK.binders, QK.sealed,
  // A binder layout's held / missing / unplaced follow the binder's lines.
  QK_BINDER_LAYOUT,
]
/** What any line edit moves: the lists (rows, summary, highlights), the card's lines, the name-level info. */
const INVALIDATE_LINE: readonly string[] = [QK.copies, QK.copiesDetail, QK.collectionInfo]
/** ...and what a change of quantity, status, finish, printing or binder moves too. */
const INVALIDATE_HOLDING: readonly string[] = [
  ...INVALIDATE_LINE, QK.copiesFacets, QK.valueHistory, QK.deckLinks, QK.deckOwnership, QK.binders, QK_BINDER_LAYOUT,
]

/**
 * The roots one confirmed PATCH re-reads, by the fields it changed. Sealed
 * products are never moved by a copy line's PATCH. A purchase price or
 * currency moves the value history (its cost basis and gain); a condition or
 * language moves smart-binder membership, so the binders' counts and values.
 * A deleted line (qty 0) re-reads everything (affectsOf).
 */
export function patchAffects(body: Omit<PatchBody, 'op_key'>): readonly string[] {
  const holding = body.qty !== undefined || body.status !== undefined || body.finish !== undefined
    || body.scryfall_id !== undefined || body.set !== undefined || body.collector_number !== undefined
    || body.split_qty !== undefined || body.binder_id !== undefined
  if (holding) return INVALIDATE_HOLDING
  const roots = [...INVALIDATE_LINE]
  if (body.purchase_price !== undefined || body.purchase_currency !== undefined) roots.push(QK.valueHistory)
  if (body.condition !== undefined || body.language !== undefined) roots.push(QK.binders)
  return roots
}

/**
 * The write runner every per-copy surface shares (copies, binders, sealed):
 * capability check, the write fence, optimistic snapshot and rollback, ONE
 * op_key per action, the ambiguous-write reconcile and the kept Retry
 * intents. `run` returns failures as values (see the module comment).
 */
export function useCopiesWriteRunner(collectionId: string | null, userId: string | null) {
  const qc = useQueryClient()

  const invalidate = useCallback((names: boolean, roots: readonly string[] = INVALIDATE_ALL) => {
    if (!collectionId) return
    // By default every root: deck-links (a qty change moves have/missing
    // even when the owned-name set did not change), binder counts and values,
    // and sealed rows (they feed the value history). A PATCH narrows it to
    // what its fields can move (patchAffects).
    for (const root of roots) {
      void qc.invalidateQueries({ queryKey: [root, collectionId] })
    }
    if (names) scheduleOwnedNamesRevalidate()
  }, [qc, collectionId])

  /** After a confirmed write whose per-copy UI is gone (C after a downgrade). */
  const invalidateConfirmed = useCallback((names: boolean) => {
    if (!collectionId) return
    for (const root of [QK.collectionInfo, QK.valueHistory, QK.deckLinks, QK.deckOwnership]) {
      void qc.invalidateQueries({ queryKey: [root, collectionId] })
    }
    if (names) scheduleOwnedNamesRevalidate()
  }, [qc, collectionId])

  /**
   * Resolve an AMBIGUOUS write (see the section comment) by re-sending the
   * SAME request (same op_key; a DELETE is idempotent) with backoff until the
   * server answers authoritatively. Not cancelled by Remove: the write stays
   * registered (Remove fails retryably meanwhile) until this settles.
   */
  const reconcile = useCallback(async <T,>(spec: RunSpec<T>, opKey: string | undefined): Promise<ReconcileOutcome<T>> => {
    if (!collectionId || !userId) return { kind: RECONCILE.identity }
    // ONE absolute deadline, whatever the capability does meanwhile: a
    // downgrade stops the per-copy UI but not this (the request may still
    // land, so Remove must stay excluded until an answer or the deadline).
    const deadline = reconcileNow() + RECONCILE_DEADLINE_MS
    // Until the deadline: only an authoritative answer or the deadline ends
    // it. The backoff saturates at its last step (16 s).
    for (let attempt = 0; ; attempt++) {
      const backoff = RECONCILE_BACKOFF_MS[Math.min(attempt, RECONCILE_BACKOFF_MS.length - 1)]
      const before = deadline - reconcileNow()
      if (before <= 0) return { kind: RECONCILE.gaveUp }
      await reconcileSleep(Math.min(backoff, before))
      const remaining = deadline - reconcileNow()
      if (remaining <= 0) return { kind: RECONCILE.gaveUp }
      // Never send as anyone else; nothing more can be learned then.
      // The collection OWNER, as everywhere (an anonymous session is a guest).
      if (collectionOwnerOf(getSupabaseIdentity()) !== userId) return { kind: RECONCILE.identity }
      // A single request with a HARD bound (never past the deadline): even a
      // session read that never returns cannot hold this attempt open.
      const attemptAbort = new AbortController()
      const bounded = boundedAttempt(
        spec.send({ userId, collectionId, signal: attemptAbort.signal, singleAttempt: true }, opKey),
        attemptAbort, Math.min(RECONCILE_REQUEST_TIMEOUT_MS, remaining),
      )
      try {
        return { kind: RECONCILE.confirmed, value: await bounded.promise }
      } catch (e) {
        const err = isCopiesError(e) ? e : new CopiesError(ERR.network)
        if (isReconcileAmbiguous(err.kind)) continue
        return { kind: RECONCILE.definitive, error: err }
      }
    }
  }, [collectionId, userId])

  const run = useCallback(async <T,>(spec: RunSpec<T>, fixedOpKey?: string, isRetry = false): Promise<WriteResult<T>> => {
    if (!collectionId || !userId) return { ok: false, reason: WRITE_FAIL.blocked }
    if (getCapability(userId, collectionId).state !== CAPABILITY.perCopy) return { ok: false, reason: WRITE_FAIL.blocked }
    if (spec.stillValid && !spec.stillValid()) return { ok: false, reason: WRITE_FAIL.blocked }
    // A line with an UNRESOLVED kept intent (an unconfirmed change that may
    // have landed) takes no new write until that intent's own Retry settles
    // or the user dismisses it: a relative change (+1 / -1) built on a qty
    // we cannot vouch for could apply twice. Only the intent's Retry passes.
    const lines = spec.pendingIds ?? (spec.queueCopyId !== undefined ? [spec.queueCopyId] : [])
    if (!isRetry && lines.length && hasUnresolvedIntent(userId, collectionId, lines)) {
      return { ok: false, reason: WRITE_FAIL.error, error: new CopiesError(ERR.retryPending) }
    }
    // (2) Fence BEFORE any optimistic change.
    const fence = tryBeginCopiesWrite(userId, collectionId)
    if (!fence) return { ok: false, reason: WRITE_FAIL.blocked }
    let snap: Snapshot | null = null
    // The session the snapshot was taken in (Codex r3 #5): it is restored
    // only while that session is still live. After a purge, another account
    // or the collection replaced it is DISCARDED, never restored: the
    // entries it covered are dropped instead, so no departed owner's data
    // is ever written back into the cache.
    let snapSession: SessionBinding = DEAD_SESSION
    const rollback = () => {
      if (snap) {
        if (snapSession.live()) {
          restore(qc, snap)
          spec.afterRollback?.()
        } else {
          for (const [key] of snap) qc.removeQueries({ queryKey: key, exact: true })
        }
      }
      snap = null
    }
    // The request's signal aborts on a Remove (the fence's signal) AND on a
    // capability downgrade of this pair: a 404 anywhere turns every active
    // per-copy write off.
    const local = new AbortController()
    const onFenceAbort = () => local.abort()
    fence.signal.addEventListener('abort', onFenceAbort)
    const downgraded = () => isDowngraded(getCapability(userId, collectionId).state)
    const unsubscribe = subscribeCapability(() => { if (downgraded()) local.abort() })
    // A line that is definitively gone takes its kept Retry intents with it.
    const lineGone = () => {
      if (spec.queueCopyId !== undefined) clearRetryIntents({ userId, collectionId, copyId: spec.queueCopyId })
    }
    const handleError = (err: CopiesError): WriteFailure => {
      if (err.kind === ERR.copyNotFound) {
        invalidate(false)
        lineGone()
      }
      if (err.kind === ERR.binderNotFound || err.kind === ERR.sealedNotFound) invalidate(false)
      if (err.kind === ERR.conflict && err.detail === DETAIL.qtyMismatch) {
        // Nothing was written. The rollback already ran; take the server's
        // qty into the line now (every copy-line surface: the detail -/+,
        // split, move, the deck editor) and refetch.
        const current = qtyMismatchCurrent(err)
        if (spec.queueCopyId !== undefined && current !== null) applyCurrentQty(qc, collectionId, spec.queueCopyId, current)
        invalidate(false)
      }
      if (err.kind === ERR.opKeyInUse) {
        console.warn('[copies] op_key_in_use: the same key was sent with a different request')
        invalidate(false)
      }
      return { ok: false, reason: WRITE_FAIL.error, error: err }
    }
    try {
      if (spec.optimistic) {
        // An in-flight refetch must not overwrite the optimistic change.
        await Promise.all([
          qc.cancelQueries({ queryKey: [QK.copies, collectionId] }),
          qc.cancelQueries({ queryKey: [QK.copiesDetail, collectionId] }),
        ])
        if (fence.fenced()) return { ok: false, reason: WRITE_FAIL.blocked }
        if (spec.stillValid && !spec.stillValid()) return { ok: false, reason: WRITE_FAIL.blocked }
        snapSession = sessionBinding(userId, collectionId)
        snap = snapshotOf(qc, collectionId)
        spec.optimistic()
      }
      // Re-checked once more before anything is sent.
      if (spec.stillValid && !spec.stillValid()) { rollback(); return { ok: false, reason: WRITE_FAIL.blocked } }
      // (4) ONE key for this action, reused only by its own retries (and by
      // the Retry of an unconfirmed change, which passes it back in).
      const opKey = spec.withOpKey ? (fixedOpKey ?? newOpKey()) : undefined
      let value: T | undefined
      for (let attempt = 0; ; attempt++) {
        // Each attempt: the write's own signal (Remove, downgrade) plus a
        // per-request timeout. A timeout is AMBIGUOUS, never a definitive
        // failure: the request may have reached the server.
        const attemptAbort = new AbortController()
        const onLocalAbort = () => attemptAbort.abort()
        local.signal.addEventListener('abort', onLocalAbort)
        if (local.signal.aborted) attemptAbort.abort()
        const ctx: CopiesContext = { userId, collectionId, signal: attemptAbort.signal }
        // HARD bound: a hung session read or refresh cannot keep the write
        // registered past it, and the abandoned attempt can never send later.
        const bounded = boundedAttempt(spec.send(ctx, opKey), attemptAbort, WRITE_REQUEST_TIMEOUT_MS)
        let err: CopiesError
        try {
          value = await bounded.promise
          break
        } catch (e) {
          err = bounded.timedOut() ? new CopiesError(ERR.network) : isCopiesError(e) ? e : new CopiesError(ERR.network)
        } finally {
          local.signal.removeEventListener('abort', onLocalAbort)
        }
        // (5) A Remove, a clear or an identity change while in the air:
        // drop everything (Remove aborts what it waits for, C3).
        if (fence.fenced()) { rollback(); return { ok: false, reason: WRITE_FAIL.blocked } }
        // A downgrade aborted it mid-flight: the request may still land.
        // The per-copy UI stops (rolled back below), but the write is NOT
        // released: it is reconciled to an answer or its deadline.
        const downgradeAbort = err.kind === ERR.aborted && downgraded()
        // An unreadable body (decode) can only come from a 2xx: the server
        // committed SOMETHING, so it is ambiguous like a lost response and is
        // reconciled with the same op_key, never rolled back and re-keyed.
        const ambiguous = isAmbiguous(err.kind) || err.kind === ERR.decode || downgradeAbort
        if (!ambiguous) { rollback(); return handleError(err) }
        // Immediate retries only while still per_copy.
        if (!downgradeAbort && !downgraded() && attempt < MAX_ACTION_RETRIES) continue
        if (downgraded()) rollback()
        // AMBIGUOUS: the server may have it. Keep the optimistic state, show
        // the lines as "Saving..." and reconcile with the same request.
        // No failure is reported unless the reconcile proves one.
        setPending(collectionId, spec.pendingIds, true)
        let outcome: ReconcileOutcome<T>
        try {
          outcome = await reconcile(spec, opKey)
        } finally {
          setPending(collectionId, spec.pendingIds, false)
        }
        if (outcome.kind === RECONCILE.confirmed) {
          // The server has it. Remove may have aborted (and given up on)
          // this write meanwhile; that abort does not undo the change, so
          // only a clear, an identity change or a downgrade stops it here.
          if (fence.fencedIgnoringAbort()) { rollback(); return { ok: false, reason: WRITE_FAIL.blocked } }
          if (downgraded()) {
            rollback()
            // Confirmed by the server: a canonical sync may see its new version.
            noteOwnCopiesChange(collectionId)
            invalidateConfirmed(spec.namesChanged(outcome.value))
            return { ok: false, reason: WRITE_FAIL.blocked }
          }
          // Confirmed by the server: a canonical sync may see its new version.
          noteOwnCopiesChange(collectionId)
          invalidate(spec.namesChanged(outcome.value), affectsOf(spec, outcome.value))
          if (spec.removedFor?.(outcome.value)) lineGone()
          return { ok: true, value: outcome.value }
        }
        rollback()
        if (outcome.kind === RECONCILE.definitive) {
          // Restore server truth (a hidden DELETE comes back from the refetch).
          invalidate(false)
          return handleError(outcome.error)
        }
        if (outcome.kind === RECONCILE.identity) return { ok: false, reason: WRITE_FAIL.blocked }
        // Gave up: show server truth (refetch) and offer a REAL Retry with
        // the SAME op_key (a replay if it did land, the write if it did not).
        invalidate(false)
        // The Retry re-runs it with the SAME op_key, through the line's
        // queue, and reports a confirmed removal (for the Undo toast).
        // Its result keeps the operation's own response (value) and is also
        // emitted for the surface that started it (the bulk bar).
        let intent: RetryIntent | null = null
        const retry = async (): Promise<RetryResult> => {
          const exec = () => run(spec, opKey, true)
          const r = spec.queueCopyId !== undefined
            ? await enqueueBound(queueKey(collectionId, spec.queueCopyId), sessionBinding(userId, collectionId), exec)
            : await exec()
          let result: RetryResult
          if (r.ok) {
            const removed = spec.removedFor?.(r.value)
            result = removed ? { ok: true, value: r.value, removed } : { ok: true, value: r.value }
          } else {
            result = r
          }
          if (intent) emitIntentSettled({ intentId: intent.id, result })
          return result
        }
        const failure: WriteFailure = { ok: false, reason: WRITE_FAIL.error, error: new CopiesError(ERR.unconfirmed), retry }
        // Kept per (user, collection) until dismissed or settled, so the
        // per-copy screen of THIS pair offers it even across an unmount.
        intent = addRetryIntent({ userId, collectionId, copyId: spec.queueCopyId, copyIds: spec.pendingIds, kind: spec.kind, meta: spec.meta, failure, retry })
        return { ...failure, intentId: intent.id }
      }
      // (5) The response arrived, but a Remove, an identity change or a clear
      // landed before this continuation: apply nothing.
      if (fence.fenced()) { rollback(); return { ok: false, reason: WRITE_FAIL.blocked } }
      if (downgraded() || local.signal.aborted) {
        // A downgrade landed after the server CONFIRMED the write. The
        // per-copy surfaces are going away (the optimistic list change is
        // rolled back and the UI is told `blocked`), but the server DID
        // change: the name-level view must not stay stale. Refresh the
        // collection info and value caches, and when the owned-name set may
        // have moved (names_changed, or any DELETE), schedule the canonical
        // owned-names revalidation.
        rollback()
        // Confirmed by the server: a canonical sync may see its new version.
        noteOwnCopiesChange(collectionId)
        invalidateConfirmed(spec.namesChanged(value as T))
        return { ok: false, reason: WRITE_FAIL.blocked }
      }
      // Confirmed by the server: a canonical sync may see its new version.
      noteOwnCopiesChange(collectionId)
      invalidate(spec.namesChanged(value as T), affectsOf(spec, value as T))
      if (spec.removedFor?.(value as T)) lineGone()
      return { ok: true, value: value as T }
    } finally {
      unsubscribe()
      fence.signal.removeEventListener('abort', onFenceAbort)
      fence.end()
    }
  }, [qc, collectionId, userId, invalidate, invalidateConfirmed, reconcile])

  return useMemo(() => ({ run, invalidate }), [run, invalidate])
}

export function useCopyMutations(collectionId: string | null, userId: string | null): CopyMutations {
  const qc = useQueryClient()
  const { run } = useCopiesWriteRunner(collectionId, userId)

  // The write itself; callers go through `patch` (serialized per line).
  // A line with tracked changes (queuePatch) shows its recomputed display
  // (baseline + every pending change, this one included) at its optimistic
  // step, so the line keeps showing the newest state after the refetches
  // this write cancels; any other line takes this edit alone, as before.
  const patchNow = useCallback(async (
    item: CopyItem, body: Omit<PatchBody, 'op_key'>, display?: PrintingDisplay,
    tracking?: { key: string; state: LineEdits; seq: number }, session?: SessionBinding,
  ) => {
    // The tracked state, only while it is still live (owner, epoch,
    // incarnation, write fence): a dead one never touches a cache.
    const live = () => (tracking && lineEditsLive(tracking.key, tracking.state) ? tracking.state : null)
    const r = await run<PatchResponse>({
      withOpKey: true,
      kind: OP_KIND.patch,
      // Every queued change needs its queue-time session (Codex r3 #6); this
      // also holds for its later Retry, which re-runs this spec.
      stillValid: session ? session.live : undefined,
      optimistic: () => {
        const tracked = live()
        if (tracked) showLine(qc, collectionId!, tracked)
        else if (!tracking) editOptimistic(qc, collectionId!, lineEdit(item.copy_id, body, display))
      },
      send: (ctx, opKey) => patchCopy(ctx, item.copy_id, { ...body, ...(opKey ? { op_key: opKey } : {}) }),
      namesChanged: v => v.names_changed || v.deleted === true,
      pendingIds: [item.copy_id],
      queueCopyId: item.copy_id,
      removedFor: v => (v.deleted ? { item } : undefined),
      affects: patchAffects(body),
      afterRollback: () => {
        const tracked = live()
        if (tracked && tracking) showLine(qc, collectionId!, tracked, tracking.seq)
      },
    })
    return withRemoved(r, item)
  }, [run, qc, collectionId])

  /**
   * Queue one PATCH on its line and show it AT ONCE: the line displays its
   * confirmed baseline with every pending change applied (lib/copyLineEdits).
   * The request itself still waits its turn, exactly as before. A confirmed
   * change moves the baseline; one that does not land (refused, blocked,
   * unconfirmed) is removed and the line recomputed from the baseline and
   * the changes still pending, then read again from the server. A 409
   * qty_mismatch moves the baseline's qty to the server's. A qty of 0 (a
   * removal) is never shown early.
   *
   * Shown early ONLY when the write itself could start now (Codex r2 #5):
   * per_copy, no unresolved kept intent on the line, and the write fence's
   * own gates (leader tab, no removal, the live user and pointer). Anything
   * else is queued untracked and the runner refuses it as before, without a
   * cache ever changing.
   */
  const queuePatch = useCallback((item: CopyItem, body: Omit<PatchBody, 'op_key'>, display?: PrintingDisplay) => {
    const key = queueKey(collectionId ?? '', item.copy_id)
    const session = sessionBinding(userId, collectionId)
    const untracked = () => enqueueBound(key, session, b => patchNow(item, body, display, undefined, b))
    if (!collectionId || !userId || body.qty === 0) return untracked()
    if (getCapability(userId, collectionId).state !== CAPABILITY.perCopy) return untracked()
    if (hasUnresolvedIntent(userId, collectionId, [item.copy_id])) return untracked()
    const fence = captureCopiesWriteContext(userId, collectionId)
    if (!fence) return untracked()
    const stateKey = lineEditsKey(userId, collectionId, item.copy_id)
    let state = getLineEdits<CopyItem, LineEdit>(stateKey)
    if (state && !lineEditsLive(stateKey, state)) state = undefined
    if (!state) {
      const base = cachedLine(qc, collectionId, item.copy_id)
      // Not on screen anywhere: nothing to keep consistent, the write's own optimistic step applies it.
      if (!base) return untracked()
      state = {
        owner: userId, collectionId, epoch: lineEditsEpoch(), incarnation: currentIncarnation(),
        fenceLive: () => fence.live() && getCapability(userId, collectionId).state === CAPABILITY.perCopy,
        base, edits: [], touched: new Set(),
      }
      setLineEdits(stateKey, state)
    }
    const seq = ++lineEditSeq
    state.edits.push({ seq, body, display })
    for (const k of fieldsOf(body)) state.touched.add(k)
    // Behind a write in flight: shown now. Otherwise the write's own
    // optimistic step shows it a moment later, AFTER its rollback snapshot,
    // so a refused or fenced first write restores a clean line.
    if ((copyQueues.get(key)?.pending ?? 0) > 0) showLine(qc, collectionId, state)
    const tracked = state
    // A TRACKED change whose line state died by its turn is refused, never
    // sent as an untracked one (Codex r3 #6).
    type PatchResult = Awaited<ReturnType<typeof patchNow>>
    return enqueueBound<PatchResult | WriteFailure>(key, session, b => (lineEditsLive(stateKey, tracked)
      ? patchNow(item, body, display, { key: stateKey, state: tracked, seq }, b)
      : Promise.resolve(BLOCKED))).then(r => {
      // A departed owner, another session, a fenced or downgraded pair: the
      // state is dead and nothing of it is shown, replayed or settled. A
      // change it showed early may still sit in that owner's cached lists:
      // they are marked stale (re-read under the live identity only).
      if (!lineEditsLive(stateKey, tracked)) {
        if (!r.ok) for (const root of [QK.copies, QK.copiesDetail]) void qc.invalidateQueries({ queryKey: [root, collectionId, tracked.owner] })
        return r
      }
      const edit = tracked.edits.find(e => e.seq === seq)
      tracked.edits = tracked.edits.filter(e => e.seq !== seq)
      if (r.ok) {
        if (edit) tracked.base = applyPatch(tracked.base, edit.body, edit.display)
      } else {
        const current = qtyMismatchCurrent(r)
        if (current !== null) tracked.base = { ...tracked.base, qty: current }
        if (current === null || current > 0) showLine(qc, collectionId, tracked)
        for (const root of [QK.copies, QK.copiesDetail]) void qc.invalidateQueries({ queryKey: [root, collectionId] })
      }
      if (tracked.edits.length === 0) deleteLineEdits(stateKey, tracked)
      return r
    })
  }, [collectionId, userId, patchNow, qc])

  const patch = useCallback((item: CopyItem, body: Omit<PatchBody, 'op_key'>, display?: PrintingDisplay) => (
    queuePatch(item, body, display)
  ), [queuePatch])

  const setQty = useCallback((item: CopyItem, qty: number, opts?: SetQtyOpts) => {
    const key = queueKey(collectionId ?? '', item.copy_id)
    const target = Math.max(0, qty)
    const expected = opts?.expectedQty
    // Record the intended value BEFORE queueing, so a relative change queued
    // right after builds on it.
    const q = copyQueues.get(key)
    const p = queuePatch(item, expected === undefined ? { qty: target } : { qty: target, expected_qty: expected })
    ;(q ?? copyQueues.get(key))!.qty = target
    return p
  }, [collectionId, queuePatch])

  const adjustQty = useCallback((item: CopyItem, delta: number, opts?: AdjustQtyOpts) => {
    const pending = copyQueues.get(queueKey(collectionId ?? '', item.copy_id))?.qty
    const base = pending ?? item.qty
    // With expectQty the base goes along as expected_qty: a queued change
    // expects the one before it to have landed, so if that one failed (or
    // another device moved the line) this one is a 409, not a blind write.
    return setQty(item, base + delta, opts?.expectQty ? { expectedQty: base } : undefined)
  }, [collectionId, setQty])

  const setStatus = useCallback((item: CopyItem, status: CopyStatus) => patch(item, { status }), [patch])

  const split = useCallback((item: CopyItem, splitQty: number, changes: Omit<PatchBody, 'op_key' | 'split_qty' | 'qty'>) => (
    enqueueBound(queueKey(collectionId ?? '', item.copy_id), sessionBinding(userId, collectionId), b => run<PatchResponse>({
      stillValid: b.live,
      withOpKey: true,
      kind: OP_KIND.split,
      // expected_qty: the qty the split was derived from (the form caps it at
      // qty - 1). A line another device changed is a 409, never a split of
      // a qty the user did not see.
      send: (ctx, opKey) => patchCopy(ctx, item.copy_id, { ...changes, split_qty: splitQty, expected_qty: item.qty, ...(opKey ? { op_key: opKey } : {}) }),
      namesChanged: v => v.names_changed,
      pendingIds: [item.copy_id],
      queueCopyId: item.copy_id,
    }))
  ), [run, collectionId, userId])

  // Move a line (or `splitQty` of it) into a manual binder, or to Unsorted
  // (null). Membership only: names never change. The response's `move` says
  // where the quantity landed (itself, a split, or a merge into an existing
  // line of the destination, which deletes the source when all of it went).
  const move = useCallback((item: CopyItem, binderId: number | null, splitQty?: number) => (
    enqueueBound(queueKey(collectionId ?? '', item.copy_id), sessionBinding(userId, collectionId), b => run<PatchResponse>({
      stillValid: b.live,
      withOpKey: true,
      kind: OP_KIND.move,
      optimistic: splitQty === undefined || splitQty >= item.qty
        ? () => editOptimistic(qc, collectionId!, items => items.map(i => (i.copy_id === item.copy_id ? { ...i, binder_id: binderId } : i)))
        : undefined,
      send: (ctx, opKey) => patchCopy(ctx, item.copy_id, {
        binder_id: binderId,
        // The qty the user saw: a line another device changed meanwhile is a
        // 409 qty_mismatch (nothing moves), refetched and reconfirmed.
        expected_qty: item.qty,
        ...(splitQty !== undefined && splitQty < item.qty ? { split_qty: splitQty } : {}),
        ...(opKey ? { op_key: opKey } : {}),
      }),
      namesChanged: () => false,
      pendingIds: [item.copy_id],
      queueCopyId: item.copy_id,
    }))
  ), [run, qc, collectionId, userId])

  const remove = useCallback((item: CopyItem): Promise<RemoveResult> => (
    enqueueBound(queueKey(collectionId ?? '', item.copy_id), sessionBinding(userId, collectionId), async (b): Promise<RemoveResult> => {
      const r = await run<void>({
        stillValid: b.live,
        // DELETE never carries an op_key (C5); it is idempotent, so a retry is safe.
        withOpKey: false,
        kind: OP_KIND.delete,
        optimistic: () => editOptimistic(qc, collectionId!, items => items.filter(i => i.copy_id !== item.copy_id)),
        send: ctx => deleteCopy(ctx, item.copy_id),
        // A DELETE is a bare 204: treat it as possibly membership-changing.
        namesChanged: () => true,
        pendingIds: [item.copy_id],
        queueCopyId: item.copy_id,
        removedFor: () => ({ item }),
      })
      if (!r.ok) return r
      // Only now, confirmed, does the caller get something to undo.
      return { ok: true, removed: { item } }
    })
  ), [run, qc, collectionId, userId])

  const add = useCallback((items: AddItem[], source?: AddSource) => run<AddResponse>({
    withOpKey: true,
    kind: OP_KIND.add,
    send: (ctx, opKey) => addCopies(ctx, items, { source, opKey }),
    namesChanged: v => v.names_changed,
  }), [run])

  // Undo = a NEW add of the removed line's attributes under a FRESH op_key,
  // source manual. `created` and `merged` are both success; the list refetches.
  // No "same copy_id" promise (the server may merge it into a manual line).
  const undo = useCallback(async (removed: RemovedCopy): Promise<WriteResult<AddResponse>> => {
    const r = await run<AddResponse>({
      withOpKey: true,
      kind: OP_KIND.undo,
      send: (ctx, opKey) => addCopies(ctx, [addItemFor(removed.item)], { source: ADD_SOURCES[0], opKey }),
      namesChanged: v => v.names_changed,
    })
    // Restored only when the line came back (created) or joined one (merged).
    // An errors-only 200 restored nothing: a failure the toast can show and
    // retry from.
    if (r.ok && r.value.created.length + r.value.merged.length === 0) {
      return {
        ok: false,
        reason: WRITE_FAIL.error,
        error: new CopiesError(ERR.validation, { detail: r.value.errors[0]?.code ?? ITEM_ERROR_CODES[0] }),
      }
    }
    return r
  }, [run])

  const bulk = useCallback((copyIds: number[], action: BulkAction, value?: BulkValue) => {
    const ids = new Set(copyIds)
    return run<BulkResponse>({
      withOpKey: true,
      kind: OP_KIND.bulk,
      optimistic: action === 'delete'
        ? () => editOptimistic(qc, collectionId!, items => items.filter(i => !ids.has(i.copy_id)))
        : action === 'set_binder' && (typeof value === 'number' || value === null)
          ? () => editOptimistic(qc, collectionId!, items => items.map(i => (ids.has(i.copy_id) ? { ...i, binder_id: value } : i)))
        : action === 'set_status' && typeof value === 'string'
          ? () => editOptimistic(qc, collectionId!, items => items.map(i => (ids.has(i.copy_id) ? { ...i, status: value as CopyStatus } : i)))
          : undefined,
      send: (ctx, opKey) => bulkCopies(ctx, { copyIds, action, value, opKey }),
      pendingIds: copyIds,
      namesChanged: v => v.names_changed || v.deleted > 0,
    })
  }, [run, qc, collectionId])

  // Remove or move from a list. Nothing optimistic: the server decides per
  // line (a stale expected_qty is a skip), so the lists refetch the answer.
  // `opKey` is always set here (withOpKey): the route requires one.
  const take = useCallback((items: TakeItem[], action: TakeAction, binderId?: number | null, meta?: TakeMeta) => run<TakeResponse>({
    withOpKey: true,
    kind: OP_KIND.take,
    meta,
    send: (ctx, opKey) => takeCopies(ctx, { items, action, binderId, opKey: opKey ?? newOpKey() }),
    pendingIds: items.map(i => i.copy_id),
    namesChanged: v => v.names_changed || v.deleted > 0,
  }), [run])

  // A take's Undo: a NEW action, so a fresh op_key per tap; its own retries
  // and a kept Retry intent re-send the same one (the server replays a
  // success it already committed for that key).
  const undoTakeFn = useCallback((token: string) => run<TakeUndoResponse>({
    withOpKey: true,
    kind: OP_KIND.takeUndo,
    send: (ctx, opKey) => undoTake(ctx, { token, opKey: opKey ?? newOpKey() }),
    namesChanged: v => v.names_changed,
  }), [run])

  return useMemo(
    () => ({ add, patch, setQty, adjustQty, setStatus, split, move, remove, undo, bulk, take, undoTake: undoTakeFn }),
    [add, patch, setQty, adjustQty, setStatus, split, move, remove, undo, bulk, take, undoTakeFn],
  )
}
